import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    ArrowLeft, 
    Truck, 
    MapPin, 
    Calendar, 
    DollarSign, 
    FileText, 
    Download,
    Upload,
    Eye,
    Clock,
    MessageSquare,
    Receipt,
    History,
    Package,
    Building2,
    Phone,
    PhoneCall,
    FileAudio,
    Loader2,
    CheckCircle,
    Circle,
    AlertCircle,
    Mic,
    CreditCard,
    FileCheck,
    Edit3,
    ThumbsUp,
    ThumbsDown,
    Meh,
    Brain
} from 'lucide-react';
import { toast } from 'sonner';
import CallAssistPanel from '../components/CallAssistPanel';

// ==================== LOAD OUTCOME FEEDBACK ====================
function LoadOutcomeFeedback({ loadId, loadStatus, onFeedbackSubmitted }) {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [existingOutcome, setExistingOutcome] = useState(null);
    const [selectedOutcome, setSelectedOutcome] = useState(null);
    
    // Check if outcome already recorded
    useEffect(() => {
        const checkExisting = async () => {
            try {
                const res = await fetch(`${API_URL}/api/learning/load/${loadId}/outcome`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.outcome_recorded) {
                        setExistingOutcome(data.outcome);
                        setSubmitted(true);
                    }
                }
            } catch (e) {
                console.error('Failed to check outcome:', e);
            }
        };
        
        if (loadId) checkExisting();
    }, [loadId]);
    
    // Only show for completed/delivered loads
    const showFeedback = loadStatus === 'delivered' || loadStatus === 'completed';
    
    if (!showFeedback) return null;
    
    const handleSubmit = async (outcome) => {
        setSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/learning/load/${loadId}/outcome`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ outcome })
            });
            
            if (res.ok) {
                setSelectedOutcome(outcome);
                setSubmitted(true);
                toast.success(`Load rated as ${outcome}`, {
                    description: 'Thanks! This improves future AI decisions.'
                });
                onFeedbackSubmitted?.(outcome);
            } else {
                toast.error('Failed to submit feedback');
            }
        } catch (e) {
            toast.error('Failed to submit feedback');
        } finally {
            setSubmitting(false);
        }
    };
    
    const outcomeOptions = [
        { value: 'good', label: 'Good', icon: ThumbsUp, description: 'Profitable & smooth', color: 'bg-green-500/20 border-green-500/50 hover:bg-green-500/30 text-green-400' },
        { value: 'neutral', label: 'Neutral', icon: Meh, description: 'Average, no issues', color: 'bg-amber-500/20 border-amber-500/50 hover:bg-amber-500/30 text-amber-400' },
        { value: 'bad', label: 'Bad', icon: ThumbsDown, description: 'Problems occurred', color: 'bg-red-500/20 border-red-500/50 hover:bg-red-500/30 text-red-400' }
    ];
    
    const finalOutcome = existingOutcome || selectedOutcome;
    
    if (submitted && finalOutcome) {
        const selectedOption = outcomeOptions.find(o => o.value === finalOutcome);
        const Icon = selectedOption?.icon || CheckCircle;
        return (
            <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-cyan-500">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${selectedOption?.color?.split(' ')[0] || 'bg-cyan-500/20'}`}>
                                <Icon className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-zinc-300 text-sm font-medium">Load Outcome Recorded</p>
                                <p className="text-zinc-500 text-xs">Rating: {selectedOption?.label || finalOutcome}</p>
                            </div>
                        </div>
                        <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/50">
                            <Brain className="w-3 h-3 mr-1" />
                            Learning Active
                        </Badge>
                    </div>
                </CardContent>
            </Card>
        );
    }
    
    return (
        <Card className="bg-zinc-900/50 border-zinc-800 border-2 border-cyan-500/30" data-testid="load-outcome-feedback">
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                    <Brain className="w-4 h-4 text-cyan-500" />
                    How Was This Load?
                    <Badge variant="outline" className="text-xs border-cyan-500/50 text-cyan-400">AI Feedback</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <p className="text-zinc-400 text-sm mb-4">
                    Your feedback helps the AI make better decisions for future loads on this lane and broker.
                </p>
                <div className="grid grid-cols-3 gap-3">
                    {outcomeOptions.map((option) => {
                        const Icon = option.icon;
                        return (
                            <button
                                key={option.value}
                                onClick={() => handleSubmit(option.value)}
                                disabled={submitting}
                                className={`p-4 rounded-lg border text-center transition-all ${option.color} ${
                                    submitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                }`}
                                data-testid={`outcome-${option.value}`}
                            >
                                <Icon className="w-6 h-6 mx-auto mb-2" />
                                <p className="text-sm font-medium">{option.label}</p>
                                <p className="text-zinc-500 text-xs mt-1">{option.description}</p>
                            </button>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge styling
const getStatusBadge = (status) => {
    const styles = {
        'available': { bg: 'bg-zinc-600', text: 'Available' },
        'booked_pending_confirmation': { bg: 'bg-yellow-600', text: 'Staged' },
        'booked': { bg: 'bg-blue-600', text: 'Booked' },
        'dispatched': { bg: 'bg-purple-600', text: 'Dispatched' },
        'at_pickup': { bg: 'bg-orange-500', text: 'At Pickup' },
        'in_transit': { bg: 'bg-amber-600', text: 'In Transit' },
        'at_delivery': { bg: 'bg-teal-500', text: 'At Delivery' },
        'delivered': { bg: 'bg-green-600', text: 'Delivered' },
        'paperwork': { bg: 'bg-yellow-600', text: 'Paperwork' },
        'invoiced': { bg: 'bg-indigo-600', text: 'Invoiced' },
        'paid': { bg: 'bg-emerald-600', text: 'Paid' },
        'completed': { bg: 'bg-green-700', text: 'Completed' },
        'cancelled': { bg: 'bg-red-600', text: 'Cancelled' },
    };
    const style = styles[status] || styles['available'];
    return <Badge className={`${style.bg} text-white`}>{style.text}</Badge>;
};

// Timeline status order
const LIFECYCLE_STATUSES = [
    { key: 'available', label: 'Available' },
    { key: 'booked_pending_confirmation', label: 'Staged' },
    { key: 'booked', label: 'Booked' },
    { key: 'dispatched', label: 'Dispatched' },
    { key: 'at_pickup', label: 'At Pickup' },
    { key: 'in_transit', label: 'In Transit' },
    { key: 'at_delivery', label: 'At Delivery' },
    { key: 'delivered', label: 'Delivered' },
    { key: 'paperwork', label: 'Paperwork' },
    { key: 'invoiced', label: 'Invoiced' },
    { key: 'paid', label: 'Paid' },
    { key: 'completed', label: 'Completed' },
];

// ==================== OVERVIEW TAB ====================
function OverviewTab({ load, truck }) {
    return (
        <div className="space-y-6">
            {/* Route Card */}
            <Card className="bg-zinc-800/50 border-zinc-700">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-orange-400" />
                        Route Information
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <p className="text-zinc-500 text-xs">Origin</p>
                        <p className="text-white font-medium">{load.origin_city}, {load.origin_state}</p>
                    </div>
                    <div>
                        <p className="text-zinc-500 text-xs">Destination</p>
                        <p className="text-white font-medium">{load.destination_city}, {load.destination_state}</p>
                    </div>
                    <div>
                        <p className="text-zinc-500 text-xs">Distance</p>
                        <p className="text-white">{load.distance || 0} miles</p>
                    </div>
                    <div>
                        <p className="text-zinc-500 text-xs">Equipment</p>
                        <p className="text-white">{load.equipment_type || load.trailer_type || 'N/A'}</p>
                    </div>
                </CardContent>
            </Card>

            {/* Broker & Truck Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Broker */}
                <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-blue-400" />
                            Broker
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <p className="text-white font-medium">{load.broker_name || 'Unknown'}</p>
                            {load.broker_mc_number && (
                                <p className="text-zinc-500 text-sm">MC# {load.broker_mc_number}</p>
                            )}
                        </div>
                        <div className="flex gap-4">
                            <div>
                                <p className="text-zinc-500 text-xs">Credit Score</p>
                                <p className={`font-semibold ${
                                    (load.broker_credit_score || 0) >= 90 ? 'text-green-400' :
                                    (load.broker_credit_score || 0) >= 75 ? 'text-amber-400' : 'text-red-400'
                                }`}>{load.broker_credit_score || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-zinc-500 text-xs">Days to Pay</p>
                                <p className={`font-semibold ${
                                    (load.broker_days_to_pay || 0) <= 21 ? 'text-green-400' :
                                    (load.broker_days_to_pay || 0) <= 30 ? 'text-amber-400' : 'text-red-400'
                                }`}>{load.broker_days_to_pay ? `${load.broker_days_to_pay}d` : 'N/A'}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Truck */}
                <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Truck className="w-4 h-4 text-amber-400" />
                            Assigned Truck
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {truck ? (
                            <div className="space-y-2">
                                <p className="text-white font-medium">{truck.name || truck.truck_id}</p>
                                <p className="text-zinc-400 text-sm">{truck.driver_name || 'No driver assigned'}</p>
                                <div className="flex gap-4 text-sm">
                                    <span className="text-zinc-500">Type: <span className="text-white">{truck.trailer_type || 'N/A'}</span></span>
                                </div>
                            </div>
                        ) : (
                            <p className="text-zinc-500">No truck assigned</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Financial Summary */}
            <Card className="bg-zinc-800/50 border-zinc-700">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-green-400" />
                        Financial Summary
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <p className="text-zinc-500 text-xs">Gross Pay</p>
                        <p className="text-green-400 font-semibold text-lg">${(load.gross_pay || 0).toLocaleString()}</p>
                    </div>
                    <div>
                        <p className="text-zinc-500 text-xs">Rate/Mile</p>
                        <p className="text-white font-mono">
                            ${load.distance ? ((load.gross_pay || 0) / load.distance).toFixed(2) : '0.00'}/mi
                        </p>
                    </div>
                    <div>
                        <p className="text-zinc-500 text-xs">Status</p>
                        {getStatusBadge(load.status)}
                    </div>
                    <div>
                        <p className="text-zinc-500 text-xs">Load ID</p>
                        <p className="text-white font-mono text-sm">{load.load_id}</p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ==================== DOCUMENTS TAB ====================
function DocumentsTab({ load, onRefresh }) {
    const [uploading, setUploading] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(true);

    const fetchDocuments = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/load-documents?load_id=${load.load_id}`, {
                credentials: 'include'
            });
            if (res.ok) {
                const docs = await res.json();
                setDocuments(docs);
            }
        } catch (err) {
            console.error('Failed to fetch documents:', err);
        } finally {
            setLoadingDocs(false);
        }
    }, [load.load_id]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleUpload = async (docType) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf,.png,.jpg,.jpeg';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            setUploading(docType);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('document_type', docType);
            formData.append('load_id', load.load_id);

            try {
                const res = await fetch(`${API_URL}/api/documents/upload`, {
                    method: 'POST',
                    credentials: 'include',
                    body: formData
                });

                if (res.ok) {
                    toast.success(`${docType.toUpperCase()} uploaded successfully`);
                    fetchDocuments();
                    onRefresh?.();
                } else {
                    const err = await res.json();
                    toast.error(err.detail || 'Upload failed');
                }
            } catch (err) {
                toast.error('Upload failed');
            } finally {
                setUploading(null);
            }
        };
        input.click();
    };

    // Build direct file URL for viewing (avoid blob/fetch for mobile compatibility)
    const getDirectFileUrl = (doc) => {
        if (!doc?.file_path) return null;
        // Use signed file if available
        const filePath = doc.signed_file_path || doc.file_path;
        // Build direct URL - file_path is relative like "uploads/ratecons/file.pdf"
        return `${API_URL}/api/${filePath}`;
    };

    const handleDownload = async (docId, filename) => {
        try {
            const res = await fetch(`${API_URL}/api/load-documents/${docId}/download`, {
                credentials: 'include'
            });
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename || `document-${docId}`;
                a.click();
            }
        } catch (err) {
            toast.error('Download failed');
        }
    };

    const getDocByType = (type) => documents.find(d => d.document_type === type);
    const rateCon = getDocByType('ratecon');
    const bol = getDocByType('bol');
    const pod = getDocByType('pod');

    return (
        <div className="space-y-4">
            {loadingDocs ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                </div>
            ) : (
                <>
                    {/* Rate Confirmation */}
                    <Card className="bg-zinc-800/50 border-zinc-700">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded ${rateCon?.signed ? 'bg-green-600/20' : 'bg-blue-600/20'} flex items-center justify-center`}>
                                        <FileText className={`w-5 h-5 ${rateCon?.signed ? 'text-green-400' : 'text-blue-400'}`} />
                                    </div>
                                    <div>
                                        <p className="text-white font-medium">Rate Confirmation</p>
                                        <p className="text-zinc-500 text-sm">
                                            {rateCon 
                                                ? (rateCon.signed ? 'Signed' : 'Uploaded')
                                                : 'Not uploaded'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {rateCon ? (
                                        <>
                                            <Button size="sm" variant="outline" asChild>
                                                <a 
                                                    href={getDirectFileUrl(rateCon)} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    data-testid="view-ratecon-btn"
                                                >
                                                    <Eye className="w-4 h-4 mr-1" /> View
                                                </a>
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => handleDownload(rateCon.document_id, rateCon.filename)}>
                                                <Download className="w-4 h-4 mr-1" /> Download
                                            </Button>
                                        </>
                                    ) : (
                                        <Button size="sm" onClick={() => handleUpload('ratecon')} disabled={uploading === 'ratecon'}>
                                            {uploading === 'ratecon' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                                            Upload
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* BOL */}
                    <Card className="bg-zinc-800/50 border-zinc-700">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded bg-purple-600/20 flex items-center justify-center">
                                        <FileCheck className="w-5 h-5 text-purple-400" />
                                    </div>
                                    <div>
                                        <p className="text-white font-medium">Bill of Lading (BOL)</p>
                                        <p className="text-zinc-500 text-sm">
                                            {bol ? 'Uploaded' : 'Not uploaded'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {bol ? (
                                        <>
                                            <Button size="sm" variant="outline" asChild>
                                                <a 
                                                    href={getDirectFileUrl(bol)} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    data-testid="view-bol-btn"
                                                >
                                                    <Eye className="w-4 h-4 mr-1" /> View
                                                </a>
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => handleDownload(bol.document_id, bol.filename)}>
                                                <Download className="w-4 h-4 mr-1" /> Download
                                            </Button>
                                        </>
                                    ) : (
                                        <Button size="sm" onClick={() => handleUpload('bol')} disabled={uploading === 'bol'}>
                                            {uploading === 'bol' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                                            Upload
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* POD */}
                    <Card className="bg-zinc-800/50 border-zinc-700">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded bg-green-600/20 flex items-center justify-center">
                                        <CheckCircle className="w-5 h-5 text-green-400" />
                                    </div>
                                    <div>
                                        <p className="text-white font-medium">Proof of Delivery (POD)</p>
                                        <p className="text-zinc-500 text-sm">
                                            {pod ? 'Uploaded' : 'Not uploaded'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {pod ? (
                                        <>
                                            <Button size="sm" variant="outline" asChild>
                                                <a 
                                                    href={getDirectFileUrl(pod)} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    data-testid="view-pod-btn"
                                                >
                                                    <Eye className="w-4 h-4 mr-1" /> View
                                                </a>
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => handleDownload(pod.document_id, pod.filename)}>
                                                <Download className="w-4 h-4 mr-1" /> Download
                                            </Button>
                                        </>
                                    ) : (
                                        <Button size="sm" onClick={() => handleUpload('pod')} disabled={uploading === 'pod'}>
                                            {uploading === 'pod' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                                            Upload
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}

// ==================== COMMUNICATION TAB ====================
function CommunicationTab({ load }) {
    const [communications, setCommunications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCommunications = async () => {
            try {
                const res = await fetch(`${API_URL}/api/loads/${load.load_id}/communications`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    setCommunications(data.communications || []);
                }
            } catch (err) {
                console.error('Failed to fetch communications:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchCommunications();
    }, [load.load_id]);

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {communications.length === 0 ? (
                <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardContent className="p-8 text-center">
                        <MessageSquare className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                        <p className="text-zinc-400">No communication records</p>
                        <p className="text-zinc-500 text-sm mt-1">
                            Call recordings and transcripts will appear here when the load is booked through Operations.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                communications.map((comm, index) => (
                    <Card key={index} className="bg-zinc-800/50 border-zinc-700">
                        <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                                <div className={`w-10 h-10 rounded flex items-center justify-center ${
                                    comm.type === 'call' ? 'bg-green-600/20' : 'bg-blue-600/20'
                                }`}>
                                    {comm.type === 'call' ? (
                                        <Phone className="w-5 h-5 text-green-400" />
                                    ) : (
                                        <MessageSquare className="w-5 h-5 text-blue-400" />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <p className="text-white font-medium">{comm.title || 'Communication Record'}</p>
                                        <span className="text-zinc-500 text-xs">
                                            {comm.timestamp ? new Date(comm.timestamp).toLocaleString() : 'N/A'}
                                        </span>
                                    </div>
                                    {comm.transcript && (
                                        <p className="text-zinc-400 text-sm mt-2 whitespace-pre-wrap">{comm.transcript}</p>
                                    )}
                                    {comm.audio_url && (
                                        <div className="mt-3 flex gap-2">
                                            <Button size="sm" variant="outline">
                                                <FileAudio className="w-4 h-4 mr-1" /> Play Audio
                                            </Button>
                                            <Button size="sm" variant="outline">
                                                <Download className="w-4 h-4 mr-1" /> Download
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))
            )}
        </div>
    );
}

// ==================== FINANCIAL TAB ====================
function FinancialTab({ load }) {
    const [financialData, setFinancialData] = useState(null);
    const [truckProfile, setTruckProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFinancials = async () => {
            try {
                // Fetch financials
                const res = await fetch(`${API_URL}/api/loads/${load.load_id}/financials`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    setFinancialData(data);
                }
                
                // If load has a truck, fetch truck profile for commission rate
                if (load.truck_id) {
                    const truckRes = await fetch(`${API_URL}/api/trucks/${load.truck_id}`, {
                        credentials: 'include'
                    });
                    if (truckRes.ok) {
                        const truckData = await truckRes.json();
                        setTruckProfile(truckData);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch financials:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchFinancials();
    }, [load.load_id, load.truck_id]);

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
            </div>
        );
    }

    const invoice = financialData?.invoice;
    const accounting = financialData?.accounting_entry;
    
    // Calculate projected financials if no accounting entry yet
    // Use commission_snapshot (locked at booking) OR truck profile commission OR default 10%
    const grossRevenue = load.gross_pay || load.rate || 0;
    const commissionRate = load.commission_snapshot || truckProfile?.commission_percent || 10;
    const projectedCommission = accounting?.commission ?? Math.round(grossRevenue * (commissionRate / 100) * 100) / 100;
    const projectedNetToDriver = accounting?.net_to_driver ?? Math.round((grossRevenue - projectedCommission) * 100) / 100;
    const projectedTaxReserve = accounting?.tax_reserve_allocated ?? Math.round(projectedCommission * 0.25 * 100) / 100;

    return (
        <div className="space-y-4">
            {/* Invoice */}
            <Card className="bg-zinc-800/50 border-zinc-700">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-indigo-400" />
                        Invoice
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {invoice ? (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <p className="text-zinc-500 text-xs">Invoice #</p>
                                    <p className="text-white font-mono">{invoice.invoice_id || invoice.invoice_number}</p>
                                </div>
                                <div>
                                    <p className="text-zinc-500 text-xs">Amount</p>
                                    <p className="text-green-400 font-semibold">${(invoice.amount || 0).toLocaleString()}</p>
                                </div>
                                <div>
                                    <p className="text-zinc-500 text-xs">Due Date</p>
                                    <p className="text-white">{invoice.due_date || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-zinc-500 text-xs">Status</p>
                                    <Badge className={
                                        invoice.status === 'paid' ? 'bg-green-600' :
                                        invoice.status === 'overdue' ? 'bg-red-600' : 'bg-amber-600'
                                    }>{invoice.status || 'pending'}</Badge>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-zinc-500 text-sm">No invoice generated yet</p>
                    )}
                </CardContent>
            </Card>

            {/* Payment Status - Show actual or projected */}
            <Card className="bg-zinc-800/50 border-zinc-700">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-green-400" />
                        {accounting ? 'Payment Status' : 'Projected Financials'}
                        {!accounting && (
                            <span className="text-xs text-zinc-500 font-normal ml-2">
                                ({commissionRate}% commission)
                            </span>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <p className="text-zinc-500 text-xs">Gross Revenue</p>
                            <p className="text-green-400 font-semibold">
                                ${(accounting?.amount_in || grossRevenue).toLocaleString()}
                            </p>
                        </div>
                        <div>
                            <p className="text-zinc-500 text-xs">Commission</p>
                            <p className="text-amber-400">
                                ${projectedCommission.toLocaleString()}
                                {!accounting && <span className="text-xs text-zinc-500 ml-1">(est)</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-zinc-500 text-xs">Tax Reserve</p>
                            <p className="text-zinc-400">
                                ${projectedTaxReserve.toLocaleString()}
                                {!accounting && <span className="text-xs text-zinc-500 ml-1">(est)</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-zinc-500 text-xs">Net to Driver</p>
                            <p className="text-white font-semibold">
                                ${projectedNetToDriver.toLocaleString()}
                                {!accounting && <span className="text-xs text-zinc-500 ml-1">(est)</span>}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Factoring */}
            <Card className="bg-zinc-800/50 border-zinc-700">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-purple-400" />
                        Factoring
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-zinc-500 text-sm">
                        {load.factoring_used ? 'This load was factored' : 'No factoring used for this load'}
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}

// ==================== TIMELINE TAB ====================
function TimelineTab({ load }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const res = await fetch(`${API_URL}/api/loads/${load.load_id}/history`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    setHistory(data.history || []);
                }
            } catch (err) {
                console.error('Failed to fetch history:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchHistory();
    }, [load.load_id]);

    // Build timeline from history or current status
    const currentStatusIndex = LIFECYCLE_STATUSES.findIndex(s => s.key === load.status);

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
            </div>
        );
    }

    return (
        <div className="space-y-1">
            {LIFECYCLE_STATUSES.map((status, index) => {
                const historyEntry = history.find(h => h.status === status.key);
                const isPast = index < currentStatusIndex;
                const isCurrent = index === currentStatusIndex;
                const isFuture = index > currentStatusIndex;

                return (
                    <div key={status.key} className="flex items-start gap-3 py-3">
                        {/* Status Icon */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isCurrent ? 'bg-green-600 ring-2 ring-green-400/50' :
                            isPast ? 'bg-green-600/50' : 'bg-zinc-700'
                        }`}>
                            {isPast || isCurrent ? (
                                <CheckCircle className="w-4 h-4 text-white" />
                            ) : (
                                <Circle className="w-4 h-4 text-zinc-500" />
                            )}
                        </div>
                        
                        {/* Status Info */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <p className={`font-medium ${
                                    isCurrent ? 'text-green-400' :
                                    isPast ? 'text-white' : 'text-zinc-500'
                                }`}>{status.label}</p>
                                {isCurrent && (
                                    <Badge className="bg-green-600 text-xs">Current</Badge>
                                )}
                            </div>
                            {historyEntry?.timestamp && (
                                <p className="text-zinc-500 text-xs mt-0.5">
                                    {new Date(historyEntry.timestamp).toLocaleString()}
                                </p>
                            )}
                        </div>

                        {/* Connector Line */}
                        {index < LIFECYCLE_STATUSES.length - 1 && (
                            <div className="absolute left-4 mt-8 w-0.5 h-8 bg-zinc-700" />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ==================== MAIN COMPONENT ====================
export default function LoadDetailPage() {
    const { loadId } = useParams();
    const navigate = useNavigate();
    const [load, setLoad] = useState(null);
    const [truck, setTruck] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [downloading, setDownloading] = useState(false);
    const [showCallAssist, setShowCallAssist] = useState(false);

    const fetchLoad = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const loadRes = await fetch(`${API_URL}/api/loads/${loadId}`, {
                credentials: 'include'
            });

            if (!loadRes.ok) {
                if (loadRes.status === 404) throw new Error('Load not found');
                throw new Error('Failed to fetch load');
            }

            const loadData = await loadRes.json();
            setLoad(loadData);

            // Fetch truck if assigned
            const truckId = loadData.truck_id || loadData.assigned_truck_id;
            if (truckId) {
                const truckRes = await fetch(`${API_URL}/api/trucks/${truckId}`, {
                    credentials: 'include'
                });
                if (truckRes.ok) {
                    setTruck(await truckRes.json());
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [loadId]);

    useEffect(() => {
        if (loadId) fetchLoad();
    }, [loadId, fetchLoad]);

    const handleDownloadFullFile = async () => {
        setDownloading(true);
        try {
            const res = await fetch(`${API_URL}/api/loads/${loadId}/download-all`, {
                credentials: 'include'
            });
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `load-${loadId}-complete.zip`;
                a.click();
                toast.success('Download started');
            } else {
                toast.error('Download failed');
            }
        } catch (err) {
            toast.error('Download failed');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-6 text-center">
                        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                        <p className="text-red-400 mb-4">{error}</p>
                        <Button onClick={() => navigate('/loads')} variant="outline">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Loads
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!load) return null;

    return (
        <div className="p-4 md:p-6 space-y-4" data-testid="load-detail-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Button 
                        onClick={() => navigate('/loads')} 
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-white"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back
                    </Button>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-xl font-bold text-white">{load.load_id}</h1>
                            {getStatusBadge(load.status)}
                        </div>
                        <p className="text-zinc-500 text-sm">
                            {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {/* Call Assist Button - Show for available/staged loads */}
                    {['available', 'booked_pending_confirmation'].includes(load.status) && (
                        <Button 
                            onClick={() => setShowCallAssist(!showCallAssist)}
                            className={showCallAssist ? 'bg-green-600 hover:bg-green-700' : 'bg-cyan-600 hover:bg-cyan-700'}
                            data-testid="call-assist-toggle"
                        >
                            <PhoneCall className="w-4 h-4 mr-2" />
                            {showCallAssist ? 'Hide Call Assist' : 'Call Assist'}
                        </Button>
                    )}
                    <Button 
                        onClick={() => navigate(`/office-desk/${load.load_id}`)}
                        variant="outline"
                        className="border-orange-700 text-orange-400 hover:bg-orange-900/30"
                        data-testid="edit-in-office-btn"
                    >
                        <Edit3 className="w-4 h-4 mr-2" />
                        Edit in Office Desk
                    </Button>
                    <Button 
                        onClick={handleDownloadFullFile} 
                        disabled={downloading}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        {downloading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Download className="w-4 h-4 mr-2" />
                        )}
                        Download Full Load File
                    </Button>
                </div>
            </div>

            {/* Call Assist Panel */}
            {showCallAssist && (
                <CallAssistPanel 
                    loadId={load.load_id}
                    onClose={() => setShowCallAssist(false)}
                />
            )}

            {/* Load Outcome Feedback - Shows only for delivered/completed loads */}
            <LoadOutcomeFeedback 
                loadId={load.load_id} 
                loadStatus={load.status}
                onFeedbackSubmitted={(outcome) => {
                    // Optionally refresh load data after feedback
                    console.log(`Feedback submitted: ${outcome}`);
                }}
            />

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="bg-zinc-800 border border-zinc-700 w-full justify-start overflow-x-auto">
                    <TabsTrigger value="overview" className="data-[state=active]:bg-zinc-700">
                        <Package className="w-4 h-4 mr-1.5" />
                        Overview
                    </TabsTrigger>
                    <TabsTrigger value="documents" className="data-[state=active]:bg-zinc-700">
                        <FileText className="w-4 h-4 mr-1.5" />
                        Documents
                    </TabsTrigger>
                    <TabsTrigger value="communication" className="data-[state=active]:bg-zinc-700">
                        <MessageSquare className="w-4 h-4 mr-1.5" />
                        Communication
                    </TabsTrigger>
                    <TabsTrigger value="financial" className="data-[state=active]:bg-zinc-700">
                        <DollarSign className="w-4 h-4 mr-1.5" />
                        Financial
                    </TabsTrigger>
                    <TabsTrigger value="timeline" className="data-[state=active]:bg-zinc-700">
                        <History className="w-4 h-4 mr-1.5" />
                        Timeline
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-4">
                    <OverviewTab load={load} truck={truck} />
                </TabsContent>
                
                <TabsContent value="documents" className="mt-4">
                    <DocumentsTab load={load} onRefresh={fetchLoad} />
                </TabsContent>
                
                <TabsContent value="communication" className="mt-4">
                    <CommunicationTab load={load} />
                </TabsContent>
                
                <TabsContent value="financial" className="mt-4">
                    <FinancialTab load={load} />
                </TabsContent>
                
                <TabsContent value="timeline" className="mt-4">
                    <TimelineTab load={load} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
